import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  resolveChannelMeta,
  type ChannelId,
  type ChannelMeta,
  type ChannelOverrides,
} from './channel-picker';

/**
 * Externe (deeplink-)kanalen die deze view ondersteunt — webchat heeft een
 * eigen flow, repair-request idem (eigen auth-gating + flow). Het type
 * wordt gebruikt om de per-kanaal-config exhaustive te houden: een nieuw
 * deeplink-kanaal toevoegen forceert TypeScript je eraan te herinneren
 * `CHANNEL_CONFIG` uit te breiden.
 */
export type ExternalChannelId = Exclude<ChannelId, 'webchat' | 'repair-request'>;

export type ExternalChannelConfig = {
  /** Welk veld op `window.chatConfig` verplicht is voor dit kanaal. */
  field: 'whatsappNumber' | 'messengerPage';
  /** Voorbeeld-waarde die in de error-state als snippet getoond wordt. */
  example: string;
  /** Bouwt de externe-app deeplink-URL uit de config-waarde. */
  buildHref: (value: string) => string;
  /** Tekst op de CTA-knop. */
  ctaLabel: string;
  /** Korte uitleg in het paneel met de daadwerkelijke waarde. */
  buildExplain: (value: string) => string;
};

export const CHANNEL_CONFIG: Record<ExternalChannelId, ExternalChannelConfig> = {
  whatsapp: {
    field: 'whatsappNumber',
    example: `'+31 20 123 45 67'`,
    buildHref: (n) => `https://wa.me/${n.replace(/[^0-9]/g, '')}`,
    ctaLabel: 'Open WhatsApp',
    buildExplain: (n) =>
      `Je wordt doorgestuurd naar WhatsApp. Stuur ons je bericht op ${n}.`,
  },
  messenger: {
    field: 'messengerPage',
    example: `'jouw-page-naam'`,
    buildHref: (p) => `https://m.me/${p}`,
    ctaLabel: 'Open Messenger',
    buildExplain: (p) =>
      `Je wordt doorgestuurd naar Facebook Messenger. We reageren op onze pagina "${p}".`,
  },
};

export function isExternalChannel(c: ChannelId): c is ExternalChannelId {
  return c !== 'webchat' && c !== 'repair-request';
}

/**
 * <embrace-external-channel> — bevestigingsscherm voor WhatsApp/Messenger.
 * Toont een grote logo + beschrijving + CTA die naar de externe app opent.
 */
@customElement('embrace-external-channel')
export class EmbraceExternalChannel extends LitElement {
  @property({ type: String })
  channel: ChannelId = 'whatsapp';

  @property({ type: String })
  whatsappNumber = '';

  @property({ type: String })
  messengerPage = '';

  @property({ attribute: false })
  channelOverrides?: ChannelOverrides;

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .body {
      flex: 1;
      padding: 28px 22px;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 14px;
      background: var(--ec-background);
    }

    .logo {
      width: 88px;
      height: 88px;
      border-radius: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 4px;
      box-shadow: 0 10px 30px -14px rgba(12,28,71,.25);
    }

    h3 {
      margin: 4px 0 0;
      font: 700 20px/1.25 var(--ec-font-display);
      color: var(--ec-text);
    }
    p {
      margin: 0;
      font: 400 14px/1.55 var(--ec-font-family);
      color: #5E5F62;
      max-width: 34ch;
    }

    .cta {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 12px;
      color: #fff;
      text-decoration: none;
      font: 700 14px/1 var(--ec-font-family);
      margin-top: 8px;
      box-shadow: 0 10px 24px -12px rgba(0,0,0,.35);
    }
    .cta:hover { filter: brightness(1.05); }

    .error-logo {
      background: var(--ec-warning-subtle);
      color: var(--ec-warning);
      box-shadow: 0 6px 18px -10px rgba(171, 101, 15, 0.45);
    }
    .hint {
      margin-top: 4px;
      max-width: 32ch;
      color: var(--ec-text-muted);
      font: 500 13px/1.5 var(--ec-font-family);
    }
    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.9em;
      padding: 1px 6px;
      border-radius: 4px;
      background: var(--ec-surface-sunken);
      color: var(--ec-text);
    }
    .snippet {
      align-self: stretch;
      margin: 4px 0 4px;
      padding: 12px 14px;
      background: var(--ec-surface-sunken);
      border: 1px solid var(--ec-border);
      border-radius: var(--ec-radius-button);
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 13px;
      line-height: 1.5;
      color: var(--ec-text);
      white-space: pre-wrap;
      word-break: break-word;
      text-align: left;
      user-select: text;
    }
  `;

  override render() {
    if (!isExternalChannel(this.channel)) return nothing;

    const meta = resolveChannelMeta(this.channel, this.channelOverrides);
    const config = CHANNEL_CONFIG[this.channel];
    const value = this[config.field];

    if (!value) return this.renderConfigError(meta.name, config);

    return html`
      <div class="body">
        <div class="logo" style=${`background: ${meta.bg};`}>
          ${this.renderBigLogo(meta)}
        </div>
        <h3>Chat verder via ${meta.name}</h3>
        <p>${config.buildExplain(value)}<br />We reageren ${meta.meta.toLowerCase()}.</p>
        <a class="cta" href=${config.buildHref(value)} target="_blank" rel="noreferrer"
           style=${`background: ${meta.color};`}>
          ${config.ctaLabel}
        </a>
      </div>
    `;
  }

  private renderConfigError(channelName: string, config: ExternalChannelConfig) {
    return html`
      <div class="body">
        <div class="logo error-logo" role="img" aria-label="Waarschuwing">
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h3>Configuratie ontbreekt</h3>
        <p>
          Het kanaal <strong>${channelName}</strong> staat aan, maar
          <code>${config.field}</code> is niet gezet.
        </p>
        <p class="hint">Voeg toe aan <code>window.chatConfig</code>:</p>
        <pre class="snippet">${config.field}: ${config.example},</pre>
      </div>
    `;
  }

  private renderBigLogo(meta: ChannelMeta) {
    if (meta.logoUrl) {
      return html`<img src=${meta.logoUrl} alt="" width="48" height="48" />`;
    }
    if (this.channel === 'whatsapp') {
      return html`
        <svg width="48" height="48" viewBox="0 0 512 512" aria-hidden="true">
          <path fill="#25D366" d="M256 0C114.6 0 0 114.6 0 256c0 45 11.6 87.3 32 124L0 512l135-32a255 255 0 0 0 121 31c141.4 0 256-114.6 256-256S397.4 0 256 0Z"/>
          <path fill="#fff" fill-rule="evenodd" d="M256 464c-36 0-69.6-9.9-98.4-27l-68.7 18 18.3-67.2A207.1 207.1 0 0 1 48 256c0-114.9 93.1-208 208-208s208 93.1 208 208-93.1 208-208 208Zm119.3-157c-6.5-3.3-38.5-19-44.5-21.2-6-2.2-10.3-3.3-14.7 3.3-4.3 6.5-16.8 21.2-20.6 25.5-3.8 4.3-7.6 4.9-14 1.6-6.5-3.3-27.5-10.2-52.4-32.4-19.4-17.3-32.4-38.6-36.2-45.1-3.8-6.5-.4-10 2.9-13.3 3-2.9 6.5-7.6 9.8-11.4 3.3-3.8 4.3-6.5 6.5-10.9 2.2-4.3 1.1-8.2-.5-11.4-1.6-3.3-14.7-35.4-20.1-48.4-5.3-12.7-10.7-11-14.7-11.2-3.8-.2-8.1-.2-12.5-.2s-11.4 1.6-17.4 8.2c-6 6.5-22.8 22.3-22.8 54.4s23.4 63.1 26.6 67.4c3.3 4.3 46 70.3 111.5 98.6 15.6 6.7 27.7 10.7 37.2 13.7 15.6 5 29.8 4.3 41 2.6 12.5-1.9 38.5-15.7 43.9-30.9 5.4-15.2 5.4-28.3 3.8-30.9-1.6-2.7-6-4.3-12.5-7.6Z" clip-rule="evenodd"/>
        </svg>
      `;
    }
    return html`
      <svg width="48" height="48" viewBox="0 0 512 512" aria-hidden="true">
        <path fill="#0084FF" d="M256 0C112.6 0 0 105.1 0 246.8c0 74.1 30.7 138.3 80.7 182.5 4.2 3.8 6.7 9 6.9 14.6l1.4 45.7c.4 14.6 15.5 24 28.8 18.1l51-22.5c4.3-1.9 9.2-2.3 13.8-1 23.4 6.4 48.3 9.8 74.4 9.8 143.4 0 256-105.1 256-246.8S399.4 0 256 0Z"/>
        <path fill="#fff" d="m102.3 320.9 75.1-119.2c12-19 37.5-23.7 55.4-10.3l59.7 44.8a15 15 0 0 0 18.1-.1l80.7-61.2c10.8-8.2 24.8 4.7 17.6 16.2l-75.1 119.1c-12 19-37.5 23.8-55.4 10.3l-59.7-44.8a15 15 0 0 0-18.1.1l-80.7 61.2c-10.8 8.2-24.8-4.7-17.6-16.1Z"/>
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-external-channel': EmbraceExternalChannel;
  }
}
